import _ from 'lodash';

import { TeamChatSignalREventType, TeamChatSignalRMessageType } from 'constants/teamChat';
import { jsonParse } from 'utils/json';

import {
  messagePinnedAction,
  messageReceivedAction,
  messagesReadAction,
  messageUnpinnedAction,
  messageUpdatedAction,
  reactionAddedAction,
  reactionRemovedAction,
  typingStartedAction,
  typingStoppedAction,
  userOfflineAction,
  userOnlineAction,
} from 'store/teamChat/actions';

const parseMessagePayload = payload => {
  if (_.isNil(payload)) {
    return null;
  }

  if (!_.isString(payload)) {
    return payload;
  }

  try {
    return jsonParse(payload);
  } catch {
    return { type: 'Text', message: payload };
  }
};

const normalizeRealtimeMessage = payload => {
  if (!payload) {
    return null;
  }

  const chatId = payload.chatId ?? payload.ChatId ?? null;

  if (!chatId) {
    return null;
  }

  const messageId = payload.messageId ?? payload.MessageId ?? null;
  const senderId = payload.senderId ?? payload.SenderId ?? null;
  const senderMemberId = payload.senderMemberId ?? payload.SenderMemberId ?? null;
  const created = payload.created ?? payload.Created ?? null;
  const rawPayload = payload.payload ?? payload.Payload ?? null;
  const parsedPayload = parseMessagePayload(rawPayload);
  const replyToMessageId = payload.replyToMessageId ?? payload.ReplyToMessageId ?? null;
  const subjectRef = payload.subjectRef ?? payload.SubjectRef ?? null;

  return {
    chatId,
    message: {
      id: messageId,
      senderId,
      memberId: senderMemberId,
      payload: parsedPayload,
      created,
      replyToMessageId,
      subjectRef,
    },
  };
};

const normalizeReadReceipt = payload => {
  if (!payload) {
    return null;
  }

  const chatId = payload.chatId ?? payload.ChatId ?? null;

  if (!chatId) {
    return null;
  }

  return {
    chatId,
    chatUserId: payload.chatUserId ?? payload.ChatUserId ?? null,
    lastReadAt: payload.lastReadAt ?? payload.LastReadAt ?? null,
  };
};

const normalizeTypingEvent = payload => {
  if (!payload) {
    return null;
  }

  const chatId = payload.chatId ?? payload.ChatId ?? null;
  const chatUserId = payload.chatUserId ?? payload.ChatUserId ?? null;

  if (!chatId || !chatUserId) {
    return null;
  }

  return {
    chatId,
    chatUserId,
    memberId: payload.memberId ?? payload.MemberId ?? null,
  };
};

const normalizeReactionEvent = payload => {
  if (!payload) {
    return null;
  }

  const chatId = payload.chatId ?? payload.ChatId ?? null;
  const messageId = payload.messageId ?? payload.MessageId ?? null;
  const chatUserId = payload.chatUserId ?? payload.ChatUserId ?? null;
  const emoji = payload.emoji ?? payload.Emoji ?? null;

  if (!chatId || !messageId || !chatUserId || !emoji) {
    return null;
  }

  return {
    chatId,
    messageId,
    chatUserId,
    memberId: payload.memberId ?? payload.MemberId ?? null,
    emoji,
    createdAt: payload.createdAt ?? payload.CreatedAt ?? null,
  };
};

const normalizePresenceEvent = payload => {
  if (!payload) {
    return null;
  }

  const chatId = payload.chatId ?? payload.ChatId ?? null;
  const chatUserId = payload.chatUserId ?? payload.ChatUserId ?? null;

  if (!chatId || !chatUserId) {
    return null;
  }

  return {
    chatId,
    chatUserId,
    memberId: payload.memberId ?? payload.MemberId ?? null,
  };
};

const normalizePinEvent = payload => {
  if (!payload) {
    return null;
  }

  const chatId = payload.chatId ?? payload.ChatId ?? null;
  const messageId = payload.messageId ?? payload.MessageId ?? null;

  if (!chatId || !messageId) {
    return null;
  }

  return {
    chatId,
    messageId,
    pinnedAt: payload.pinnedAt ?? payload.PinnedAt ?? null,
  };
};

const normalizeMessageUpdated = payload => {
  if (!payload) {
    return null;
  }

  const chatId = payload.chatId ?? payload.ChatId ?? null;
  const messageId = payload.messageId ?? payload.MessageId ?? null;

  if (!chatId || !messageId) {
    return null;
  }

  return {
    chatId,
    messageId,
    payload: parseMessagePayload(payload.payload ?? payload.Payload ?? null),
    updated: payload.updated ?? payload.Updated ?? null,
  };
};

class SignalRTeamChatController {
  #connection;
  #dispatch;
  #logger;
  #joinedChannels = new Map();

  constructor(connection, logger) {
    this.#connection = connection;
    this.#logger = logger;
  }

  get connection() {
    if (!this.#connection) {
      this.#logger.error('Trying to use team chat SignalR connection without active connection');
    }

    return this.#connection;
  }

  init(dispatch) {
    if (!dispatch) {
      this.#logger.error('Dispatch is required for initializing team chat SignalR controller');
    }

    this.#dispatch = dispatch;
  }

  startListeners() {
    this.connection.on(TeamChatSignalREventType.RECEIVE_MESSAGE, this.#onReceiveMessage.bind(this));
    this.connection.on(TeamChatSignalREventType.MESSAGES_READ, this.#onMessagesRead.bind(this));
    this.connection.on(TeamChatSignalREventType.TYPING_STARTED, this.#onTypingStarted.bind(this));
    this.connection.on(TeamChatSignalREventType.TYPING_STOPPED, this.#onTypingStopped.bind(this));
    this.connection.on(TeamChatSignalREventType.MESSAGE_UPDATED, this.#onMessageUpdated.bind(this));
    this.connection.on(TeamChatSignalREventType.REACTION_ADDED, this.#onReactionAdded.bind(this));
    this.connection.on(
      TeamChatSignalREventType.REACTION_REMOVED,
      this.#onReactionRemoved.bind(this),
    );
    this.connection.on(TeamChatSignalREventType.USER_ONLINE, this.#onUserOnline.bind(this));
    this.connection.on(TeamChatSignalREventType.USER_OFFLINE, this.#onUserOffline.bind(this));
    this.connection.on(TeamChatSignalREventType.MESSAGE_PINNED, this.#onMessagePinned.bind(this));
    this.connection.on(
      TeamChatSignalREventType.MESSAGE_UNPINNED,
      this.#onMessageUnpinned.bind(this),
    );
  }

  stopListeners() {
    this.connection.off(TeamChatSignalREventType.RECEIVE_MESSAGE);
    this.connection.off(TeamChatSignalREventType.MESSAGES_READ);
    this.connection.off(TeamChatSignalREventType.TYPING_STARTED);
    this.connection.off(TeamChatSignalREventType.TYPING_STOPPED);
    this.connection.off(TeamChatSignalREventType.MESSAGE_UPDATED);
    this.connection.off(TeamChatSignalREventType.REACTION_ADDED);
    this.connection.off(TeamChatSignalREventType.REACTION_REMOVED);
    this.connection.off(TeamChatSignalREventType.USER_ONLINE);
    this.connection.off(TeamChatSignalREventType.USER_OFFLINE);
    this.connection.off(TeamChatSignalREventType.MESSAGE_PINNED);
    this.connection.off(TeamChatSignalREventType.MESSAGE_UNPINNED);
  }

  async joinChannel(chatId) {
    if (!chatId) {
      return;
    }

    const currentCount = this.#joinedChannels.get(chatId) ?? 0;

    this.#joinedChannels.set(chatId, currentCount + 1);

    if (currentCount > 0) {
      return;
    }

    try {
      await this.connection.invoke(TeamChatSignalRMessageType.JOIN_CHAT, chatId);
    } catch (error) {
      const nextCount = (this.#joinedChannels.get(chatId) ?? 1) - 1;

      if (nextCount <= 0) {
        this.#joinedChannels.delete(chatId);

        this.#logger.error('[TeamChat SignalR] Failed to join chat', chatId, error);

        return;
      }

      this.#joinedChannels.set(chatId, nextCount);

      this.#logger.error('[TeamChat SignalR] Failed to join chat', chatId, error);
    }
  }

  async leaveChannel(chatId) {
    if (!chatId) {
      return;
    }

    const currentCount = this.#joinedChannels.get(chatId) ?? 0;

    if (currentCount <= 0) {
      return;
    }

    const nextCount = currentCount - 1;

    if (nextCount > 0) {
      this.#joinedChannels.set(chatId, nextCount);

      return;
    }

    this.#joinedChannels.delete(chatId);

    try {
      await this.connection.invoke(TeamChatSignalRMessageType.LEAVE_CHAT, chatId);
    } catch (error) {
      this.#logger.error('[TeamChat SignalR] Failed to leave chat', chatId, error);
    }
  }

  async rejoinAllChannels() {
    const chatIds = Array.from(this.#joinedChannels.keys());

    await Promise.all(
      chatIds.map(async chatId => {
        try {
          await this.connection.invoke(TeamChatSignalRMessageType.JOIN_CHAT, chatId);
        } catch (error) {
          this.#logger.error('[TeamChat SignalR] Failed to rejoin chat', chatId, error);
        }
      }),
    );
  }

  async startTyping(chatId) {
    if (!chatId) {
      return;
    }

    try {
      await this.connection.invoke(TeamChatSignalRMessageType.START_TYPING, chatId);
    } catch (error) {
      this.#logger.error('[TeamChat SignalR] Failed to start typing', chatId, error);
    }
  }

  async stopTyping(chatId) {
    if (!chatId) {
      return;
    }

    try {
      await this.connection.invoke(TeamChatSignalRMessageType.STOP_TYPING, chatId);
    } catch (error) {
      this.#logger.error('[TeamChat SignalR] Failed to stop typing', chatId, error);
    }
  }

  #onReceiveMessage(payload) {
    const normalized = normalizeRealtimeMessage(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(messageReceivedAction(normalized));

    if (typeof window !== 'undefined' && _.isFunction(window.CustomEvent)) {
      window.dispatchEvent(
        new window.CustomEvent('teamchat:message-received', { detail: normalized }),
      );
    }
  }

  #onMessagesRead(payload) {
    const normalized = normalizeReadReceipt(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(messagesReadAction(normalized));
  }

  #onTypingStarted(payload) {
    const normalized = normalizeTypingEvent(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(typingStartedAction(normalized));
  }

  #onTypingStopped(payload) {
    const normalized = normalizeTypingEvent(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(typingStoppedAction(normalized));
  }

  #onMessageUpdated(payload) {
    const normalized = normalizeMessageUpdated(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(messageUpdatedAction(normalized));
  }

  #onReactionAdded(payload) {
    const normalized = normalizeReactionEvent(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(
      reactionAddedAction({
        chatId: normalized.chatId,
        messageId: normalized.messageId,
        reaction: {
          chatUserId: normalized.chatUserId,
          emoji: normalized.emoji,
          createdAt: normalized.createdAt,
        },
      }),
    );
  }

  #onReactionRemoved(payload) {
    const normalized = normalizeReactionEvent(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(
      reactionRemovedAction({
        chatId: normalized.chatId,
        messageId: normalized.messageId,
        chatUserId: normalized.chatUserId,
        emoji: normalized.emoji,
      }),
    );
  }

  #onUserOnline(payload) {
    const normalized = normalizePresenceEvent(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(userOnlineAction(normalized));
  }

  #onUserOffline(payload) {
    const normalized = normalizePresenceEvent(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(userOfflineAction(normalized));
  }

  #onMessagePinned(payload) {
    const normalized = normalizePinEvent(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(messagePinnedAction(normalized));
  }

  #onMessageUnpinned(payload) {
    const normalized = normalizePinEvent(payload);

    if (!this.#dispatch || !normalized) {
      return;
    }

    this.#dispatch(messageUnpinnedAction(normalized));
  }
}

export default SignalRTeamChatController;
